import { gql, useQuery } from '@apollo/client'
import { useMemo } from 'react'

import { useDateFilter } from '../../dateFilter'
import { useGroupFilter } from '../../groupFilter'
import { IApiDataType } from '../types'

const query = gql`
  query ListAcePopDailyFlashKpis(
    $iStartDate: Date!
    $iEndDate: Date!
    $iFilter: JSON!
    $hasGroupBy: Boolean!
  ) {
    listLocationGroupMetricValues(
      iStartDate: $iStartDate
      iEndDate: $iEndDate
      iFilter: $iFilter
    ) @skip(if: $hasGroupBy) {
      nodes {
        locationGroupId
        metricData
        metricSummaryData
      }
    }

    listLocationMetricValues(
      iStartDate: $iStartDate
      iEndDate: $iEndDate
      iFilter: $iFilter
    ) @include(if: $hasGroupBy) {
      nodes {
        locationId
        metricData
        metricSummaryData
      }
    }

    listEmployeeMetricValues(
      iStartDate: $iStartDate
      iEndDate: $iEndDate
      iFilter: $iFilter
    ) {
      nodes {
        metricLocationId
        employeeName
        metricData
      }
    }
  }
`

export const acePopDailyFlashConfigs = {
  netSales: 'price',
  yoyNetSalesPercentage: 'percent',
  cashDeposits: 'price',
  cashOs: 'price',
  overringAmount: 'price',
  deletesAmount: 'price',
  deletesAmountPercentage: 'percent',
  maxDeleteBy: 'string',
  maxDeleteAmount: 'price',
} as const

const KPIS = [
  'net_sales',
  'yoy_net_sales_percentage',
  'cash_deposits',
  'cash_os',
  'overring_amount',
  'deletes_amount',
  'deletes_amount_percentage',
]

const useAcePopDailyFlash = () => {
  const { startDate, endDate } = useDateFilter()
  const { groupFilter, hasGroupBy } = useGroupFilter()
  const { data, loading } = useQuery(query, {
    variables: {
      iStartDate: startDate,
      iEndDate: endDate,
      iFilter: {
        location_group_ids: hasGroupBy
          ? groupFilter?.ids
          : groupFilter?.list?.map((g) => g.id),
        metrics: KPIS,
      },
      hasGroupBy,
    },
    skip: !startDate || !endDate || !groupFilter,
  })

  return {
    data: useMemo((): IApiDataType => {
      const rawEmployeeData: any = data?.listEmployeeMetricValues?.nodes
      const customizedData: any =
        data?.[
          hasGroupBy
            ? 'listLocationMetricValues'
            : 'listLocationGroupMetricValues'
        ]?.nodes

      if (!customizedData) return null

      const source = customizedData.map((locationData: any) => {
        const groupInfo = groupFilter?.list?.find(
          (l) =>
            l.id ===
            (hasGroupBy
              ? locationData.locationId
              : locationData.locationGroupId),
        )
        const kpisData: { [key: string]: number } = {}

        for (const key in locationData.metricData) {
          if (locationData.metricData.hasOwnProperty(key)) {
            const newKey = key.replace(/_(.)/g, (_, char) => char.toUpperCase())
            switch (locationData.metricData[key].unit) {
              case 'DOLLAR':
              case 'PERCENTAGE':
                kpisData[newKey] = locationData.metricData[key].value * 100
                break

              default:
                kpisData[newKey] = locationData.metricData[key].value
                break
            }
          }
        }

        const maxEmployeeData = !hasGroupBy
          ? { employee: '', value: null }
          : rawEmployeeData?.reduce(
              (result: any, data: any) => {
                if (data.metricLocationId === locationData.locationId) {
                  if (
                    !result.value ||
                    result.value <= data.metricData?.deletes_amount.value
                  ) {
                    return {
                      employee: data.employeeName,
                      value: data.metricData?.deletes_amount.value,
                    }
                  }
                }

                return result
              },
              { employee: '', value: null },
            )

        return {
          ...kpisData,
          groupInfo,
          maxDeleteBy: maxEmployeeData?.employee,
          maxDeleteAmount: maxEmployeeData?.value
            ? maxEmployeeData.value * 100
            : null,
        }
      })

      const [rawSummary] = customizedData.filter(
        (locationData: any) => locationData.metricSummaryData != null,
      )
      const summary: { [key: string]: number } = {}
      for (const key in rawSummary?.metricSummaryData) {
        if (rawSummary.metricSummaryData.hasOwnProperty(key)) {
          const newKey = key.replace(/_(.)/g, (_, char) => char.toUpperCase())
          switch (rawSummary.metricSummaryData[key].unit) {
            case 'DOLLAR':
            case 'PERCENTAGE':
              summary[newKey] = rawSummary.metricSummaryData[key].value * 100
              break

            default:
              summary[newKey] = rawSummary.metricSummaryData[key].value
              break
          }
        }
      }

      return {
        source,
        summary: {
          ...summary,
          maxDeleteBy: null,
          maxDeleteAmount: null,
        },
      }
    }, [groupFilter, data]),
    loading,
  }
}

export default useAcePopDailyFlash
